import React, { useState, useEffect } from 'react';
import { Department, DepartmentFormData, Company } from '../../types/models';
import { DepartmentService } from '../../services/departmentService';
import { CompanyService } from '../../services/companyService';
import { useCompany } from '../../contexts/CompanyContext';

interface DepartmentFormProps {
  department?: Department;
  companyId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export const DepartmentForm: React.FC<DepartmentFormProps> = ({
  department,
  companyId,
  onSuccess,
  onCancel
}) => {
  const { selectedCompany } = useCompany();
  const [formData, setFormData] = useState<DepartmentFormData>({
    name: department?.name || '',
    companyId: department?.companyId || companyId || selectedCompany?.id || 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const result = await CompanyService.getUserCompanies();
      if (result.success && result.data) {
        setCompanies(result.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки контрагентов:', err);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (department) {
        // Обновление
        response = await DepartmentService.update(department.id, { ...formData, id: department.id });
      } else {
        // Создание
        response = await DepartmentService.create(formData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Ошибка сохранения');
      }
    } catch (err) {
      setError('Произошла ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'companyId' ? Number(value) : value
    }));
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ maxWidth: '700px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card" style={{ margin: 0, boxShadow: 'none' }}>
          <h3>{department ? 'Редактирование структурного подразделения' : 'Создание структурного подразделения'}</h3>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Название структурного подразделения *</label>
          <input
            type="text"
            name="name"
            className="form-input"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Введите название структурного подразделения"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Контрагент *</label>
          <select
            name="companyId"
            className="form-input"
            value={formData.companyId}
            onChange={handleChange}
            required
            disabled={companiesLoading}
          >
            <option value="">
              {companiesLoading ? 'Загрузка контрагентов...' : 'Выберите контрагента'}
            </option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>
                {company.shortName || company.fullName}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Сохранение...' : (department ? 'Сохранить' : 'Создать')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </button>
        </div>
      </form>
        </div>
      </div>
    </div>
  );
};

